import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { createRoom, joinRoom, listOpenRooms } from '../services/lobby';
import { usePostHog } from 'posthog-js/react';

interface GameSettings {
  wordLimit: 1 | 2 | 3;
  rounds: number;
  timeLimit: 30 | 60 | null;
  isPrivate: boolean;
  numPlayers: number; // 2-20 for speed mode, 2-4 for other modes
  autoSelectColor: boolean;
  gameMode?: 'cooperative' | 'teams' | 'speed';
}

interface OpenGame {
  matchID: string;
  players: { name?: string }[];
  createdAt: number;
}

export function Lobby() {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [username, setUsername] = useState('');
  const [roomCode, setRoomCode] = useState('');
  const [showCreateGame, setShowCreateGame] = useState(false);
  const [openGames, setOpenGames] = useState<OpenGame[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [gameSettings, setGameSettings] = useState<GameSettings>({
    wordLimit: 2,
    rounds: 8,
    timeLimit: 60,
    isPrivate: false,
    numPlayers: 2,
    autoSelectColor: true,
    gameMode: 'cooperative',
  });

  useEffect(() => {
    // Check if user has a username
    const storedUsername = sessionStorage.getItem('username');
    if (!storedUsername) {
      navigate('/');
      return;
    }
    setUsername(storedUsername);
    
    // Fetch open games
    fetchOpenGames();
    
    // Refresh open games every 5 seconds
    const interval = setInterval(fetchOpenGames, 5000);
    return () => clearInterval(interval);
  }, [navigate]);

  const fetchOpenGames = async () => {
    try {
      const games = await listOpenRooms();
      setOpenGames(games);
    } catch (err) {
      console.error('Failed to fetch open games:', err);
    }
  };

  const handleCreateGame = async () => {
    setLoading(true);
    setError('');
    
    try {
      const { roomID } = await createRoom(username, gameSettings);
      posthog?.capture('game_created', { 
        roomID, 
        gameMode: gameSettings.gameMode,
        numPlayers: gameSettings.numPlayers,
        isPrivate: gameSettings.isPrivate 
      });
      navigate(`/game/${roomID}`);
    } catch (err) {
      setError('Failed to create game. Please try again.');
      console.error('Failed to create game:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinGame = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await joinRoom(roomCode, username);
      posthog?.capture('game_joined', { roomCode, method: 'room_code' });
      navigate(`/game/${roomCode}`);
    } catch (err) {
      setError('Failed to join game. Check the room code and try again.');
      console.error('Failed to join game:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinOpenGame = async (matchID: string) => {
    setLoading(true);
    setError('');
    
    try {
      await joinRoom(matchID, username);
      navigate(`/game/${matchID}`);
    } catch (err) {
      setError('Failed to join game.');
      console.error('Failed to join game:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-gray-900">Game Lobby</h1>
            <div className="text-right">
              <p className="text-sm text-gray-600">Playing as</p>
              <p className="font-medium text-gray-900">{username}</p>
            </div>
          </div>
        </motion.div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700"
          >
            {error}
          </motion.div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {/* Create Game */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg shadow-lg p-6"
          >
            <h2 className="text-xl font-bold text-gray-900 mb-4">Create New Game</h2>
            
            {!showCreateGame ? (
              <button
                onClick={() => setShowCreateGame(true)}
                className="w-full px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
              >
                Create Game
              </button>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Game Mode
                  </label>
                  <select
                    value={gameSettings.gameMode}
                    onChange={(e) => {
                      const mode = e.target.value as 'cooperative' | 'teams' | 'speed';
                      setGameSettings({ 
                        ...gameSettings, 
                        gameMode: mode,
                        // Reset to appropriate defaults based on mode
                        numPlayers: mode === 'speed' ? 10 : (mode === 'teams' ? 4 : 2)
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="cooperative">Cooperative</option>
                    <option value="teams">Teams (2v2)</option>
                    <option value="speed">Speed Mode</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {gameSettings.gameMode === 'cooperative' && "Work together to score points!"}
                    {gameSettings.gameMode === 'teams' && "Two teams compete against each other!"}
                    {gameSettings.gameMode === 'speed' && "Race to be the first to guess correctly!"}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Number of Players
                  </label>
                  {gameSettings.gameMode === 'speed' ? (
                    <input
                      type="number"
                      min={2}
                      max={20}
                      value={gameSettings.numPlayers}
                      onChange={(e) => {
                        const numPlayers = Math.min(20, Math.max(2, parseInt(e.target.value) || 2));
                        setGameSettings({ 
                          ...gameSettings, 
                          numPlayers
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    />
                  ) : (
                    <select
                      value={gameSettings.numPlayers}
                      onChange={(e) => {
                        const numPlayers = Number(e.target.value);
                        setGameSettings({ 
                          ...gameSettings, 
                          numPlayers,
                          // Teams mode only available for 4 players
                          gameMode: gameSettings.gameMode === 'teams' && numPlayers !== 4 ? 'cooperative' : gameSettings.gameMode
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    >
                      <option value={2}>2 Players</option>
                      <option value={3}>3 Players</option>
                      <option value={4}>4 Players</option>
                    </select>
                  )}
                  {gameSettings.gameMode === 'speed' && (
                    <p className="text-xs text-gray-500 mt-1">
                      Up to {gameSettings.numPlayers} players can join. You can start with 2+.
                    </p>
                  )}
                </div>


                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Word Limit
                  </label>
                  <select
                    value={gameSettings.wordLimit}
                    onChange={(e) => setGameSettings({ ...gameSettings, wordLimit: Number(e.target.value) as 1 | 2 | 3 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  >
                    <option value={1}>1 word</option>
                    <option value={2}>2 words</option>
                    <option value={3}>3 words</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Number of Rounds
                  </label>
                  <input
                    type="number"
                    min={4}
                    max={12}
                    step={gameSettings.gameMode === 'teams' ? 4 : 1}
                    value={gameSettings.rounds}
                    onChange={(e) => setGameSettings({ ...gameSettings, rounds: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                  {gameSettings.gameMode === 'teams' && (
                    <p className="text-xs text-gray-500 mt-1">
                      Use multiples of 4 for balanced team games (4, 8, 12)
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Time Limit per Turn
                  </label>
                  <select
                    value={gameSettings.timeLimit || 'none'}
                    onChange={(e) => setGameSettings({ 
                      ...gameSettings, 
                      timeLimit: e.target.value === 'none' ? null : Number(e.target.value) as 30 | 60
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="none">No limit</option>
                    <option value={30}>30 seconds</option>
                    <option value={60}>60 seconds</option>
                  </select>
                </div>

                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={gameSettings.isPrivate}
                      onChange={(e) => setGameSettings({ ...gameSettings, isPrivate: e.target.checked })}
                      className="rounded text-orange-500 focus:ring-orange-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Private game (requires room code)</span>
                  </label>
                </div>

                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={gameSettings.autoSelectColor}
                      onChange={(e) => setGameSettings({ ...gameSettings, autoSelectColor: e.target.checked })}
                      className="rounded text-orange-500 focus:ring-orange-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Auto-select colors</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    Colors will be randomly selected for clue givers
                  </p>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={handleCreateGame}
                    disabled={loading}
                    className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
                  >
                    {loading ? 'Creating...' : 'Start Game'}
                  </button>
                  <button
                    onClick={() => setShowCreateGame(false)}
                    disabled={loading}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </motion.div>

          {/* Join Game */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg shadow-lg p-6"
          >
            <h2 className="text-xl font-bold text-gray-900 mb-4">Join Existing Game</h2>
            
            <form onSubmit={handleJoinGame} className="space-y-4">
              <div>
                <label htmlFor="roomCode" className="block text-sm font-medium text-gray-700 mb-1">
                  Room Code
                </label>
                <input
                  type="text"
                  id="roomCode"
                  value={roomCode}
                  onChange={(e) => setRoomCode(e.target.value)}
                  placeholder="Enter room code"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-center text-lg font-mono"
                />
              </div>
              
              <button
                type="submit"
                disabled={roomCode.length === 0 || loading}
                className="w-full px-6 py-3 bg-lightning-500 text-white rounded-lg hover:bg-lightning-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {loading ? 'Joining...' : 'Join Game'}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Open Games</h3>
              {openGames.length === 0 ? (
                <p className="text-sm text-gray-500">No open games available</p>
              ) : (
                <div className="space-y-2">
                  {openGames.map((game) => (
                    <div
                      key={game.matchID}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <p className="text-sm font-medium">
                          Room: {game.matchID.slice(0, 8).toUpperCase()}
                        </p>
                        <p className="text-xs text-gray-500">
                          {game.players.filter(p => p.name).length}/{game.players.length} players
                        </p>
                      </div>
                      <button
                        onClick={() => handleJoinOpenGame(game.matchID)}
                        disabled={loading}
                        className="px-3 py-1 bg-lightning-500 text-white text-sm rounded hover:bg-lightning-600 disabled:bg-gray-300 transition-colors"
                      >
                        Join
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-8 text-center"
        >
          <button
            onClick={() => {
              sessionStorage.removeItem('username');
              navigate('/');
            }}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Change username
          </button>
        </motion.div>
      </div>
    </div>
  );
}